import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, Stack, TextField, Typography } from "@mui/material";
import {
  AGENT_TASKS_UPDATED_EVENT, getPositionMonitor, notifyAgentTasksUpdated,
  runAgentTaskAction, updatePositionMonitor,
} from "../../api/agentTasks";

// Only a saved, account-owned task can produce an active card. Answer prose
// and streaming classifier signals never establish monitoring state.
export default function PositionMonitorCard({ result }) {
  const [task, setTask] = useState(result?.task || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [price, setPrice] = useState("");
  const id = result?.task_id || result?.task?.id;
  useEffect(() => {
    setTask(result?.task || null);
    if (!id) return undefined;
    let mounted = true;
    const refresh = async () => {
      if (document.hidden) return;
      try {
        const fresh = await getPositionMonitor(id);
        if (mounted) { setTask(fresh); setError(""); }
      } catch (e) {
        if (mounted) setError(`Could not refresh monitoring: ${e.message}`);
      }
    };
    refresh();
    const timer = setInterval(refresh, 60_000);
    window.addEventListener(AGENT_TASKS_UPDATED_EVENT, refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      mounted = false;
      clearInterval(timer);
      window.removeEventListener(AGENT_TASKS_UPDATED_EVENT, refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [id, result?.task?.updated_at]);

  if (!result) return null;
  if (!task) {
    if (!result.task_id && !["blocked", "failed", "needs_clarification"].includes(result.status)) return null;
    return <Box role="status" data-position-monitor="unavailable" sx={{ mt: 1, p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
      <Typography variant="body2">{error || (result.task_id ? "Checking saved monitoring…" : "Monitoring is not active. See the setup detail above.")}</Typography>
    </Box>;
  }
  const ended = ["completed", "canceled"].includes(task.status);
  const mutate = async (operation) => {
    setBusy(true); setError("");
    try {
      await operation();
      setTask(await getPositionMonitor(task.id));
      setEditing(null);
      notifyAgentTasksUpdated({ taskId: task.id });
    } catch (e) {
      setError(e.message);
      // A polling update or another device may have advanced the saved state.
      // Keep the user's edit visible while refreshing its optimistic version.
      try { setTask(await getPositionMonitor(task.id)); } catch (_) { /* retain the original error */ }
    }
    finally { setBusy(false); }
  };
  const change = (changes) => mutate(() => updatePositionMonitor({ id: task.id, changes, expectedUpdatedAt: task.updated_at }));
  const labels = { active: "Watching", queued: "Queued", running: "Reassessing", paused: "Paused", failed: "Needs attention", completed: "Ended", canceled: "Stopped" };
  return <Box data-position-monitor={task.id} data-monitor-status={task.status} sx={{ my: 1.5, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper", minWidth: 0 }}>
    <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between">
      <Typography variant="subtitle2">{task.title}</Typography>
      <Chip size="small" label={labels[task.status] || task.status} />
    </Stack>
    {task.legs?.length > 0 && <Typography variant="body2" sx={{ mt: 0.75 }}>
      {task.legs.map((leg) => `${leg.quantity > 0 ? "+" : ""}${leg.quantity} ${Number(leg.strike).toLocaleString()}${leg.type === "call" ? "C" : "P"}`).join(" · ")}
      {task.position?.quantity_basis === "per_structure" ? " · per structure" : ""}
      {task.position?.quantity_basis === "remaining_fraction_of_stated_position" ? " · original structure" : ""}
    </Typography>}
    <Box component="ul" sx={{ pl: 2.5, my: 1 }}>
      {(task.conditions || []).map((condition) => <Box component="li" key={condition.id} sx={{ mb: 0.5 }}>
        <Typography component="span" variant="body2">{condition.ticker} {condition.comparison} {Number(condition.threshold).toLocaleString()}</Typography>
        {task.continuous && !ended && <Button size="small" disabled={busy} onClick={() => { setEditing(condition.id); setPrice(String(condition.threshold)); }} aria-label={`Edit ${condition.ticker} ${condition.comparison} condition`}>Edit</Button>}
        {condition.source_phrase && <Typography variant="caption" display="block" color="text.secondary">{condition.source_phrase}</Typography>}
        {editing === condition.id && <Box component="form" onSubmit={(event) => { event.preventDefault(); change({ operation: "set_condition", condition_id: condition.id, threshold: Number(price), unit: "price" }); }} sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", my: 1 }}>
          <TextField size="small" label="Underlying price" type="number" required inputProps={{ min: 0.000001, step: "any" }} value={price} onChange={(event) => setPrice(event.target.value)} sx={{ width: 160 }} />
          <Button type="submit" disabled={busy || !Number.isFinite(Number(price)) || Number(price) <= 0}>Save price</Button>
          <Button onClick={() => setEditing(null)}>Discard edit</Button>
        </Box>}
      </Box>)}
    </Box>
    {task.continuous && ended && <Typography variant="caption" display="block" color="text.secondary">
      {task.position?.position_status === "user_reported_closed" ? "Position reported closed. Monitoring has stopped." : "Monitoring has ended. Saved conditions are shown for reference."}
    </Typography>}
    {task.continuous && !ended && <Typography variant="caption" display="block" color="text.secondary">
      {task.expires_at ? `Ends ${new Date(task.expires_at).toLocaleString()}.` : "No expiration supplied; continues until you pause or stop it."}
      {task.position?.missing_inputs?.length > 0 ? ` Still needed: ${task.position.missing_inputs.join("; ")}. Add these in chat.` : ""}
    </Typography>}
    {task.position?.remaining_fraction && <Typography variant="caption" display="block">User reported {Math.round(task.position.remaining_fraction * 100)}% remaining; contract count is unconfirmed.</Typography>}
    {task.summary && !ended && <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>Last update: {task.summary}</Typography>}
    {task.limits && !ended && <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>{task.limits}</Typography>}
    {task.continuous && !ended && <Typography variant="caption" display="block" color="text.secondary">
      {task.last_observed_at ? `Last evaluated quote: ${new Date(task.last_observed_at).toLocaleString()}.` : "Waiting for a fresh underlying-price observation."}
    </Typography>}
    <Typography variant="caption" display="block" color="text.secondary">Delivery: {(task.delivery_channels || []).join(", ") || "none configured"}.</Typography>
    {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    {!ended && <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.75 }}>
      <Button size="small" disabled={busy} onClick={() => mutate(() => runAgentTaskAction({ id: task.id, action: task.status === "paused" ? "resume" : "pause" }))}>{task.status === "paused" ? "Resume" : "Pause"}</Button>
      <Button size="small" disabled={busy} onClick={() => mutate(() => runAgentTaskAction({ id: task.id, action: "cancel" }))}>Stop monitoring</Button>
      {task.continuous && task.position?.position_status !== "hypothetical" && <Button size="small" disabled={busy} onClick={() => change({ operation: "close_position" })}>I closed this position</Button>}
    </Stack>}
  </Box>;
}
